import { useState, useEffect } from 'react';
import { Row, Col, Button, Spinner, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getAvailableSlots, getEmployeePhotoUrl } from '../../../../../services/PublicBookingService';
import { worksOnDate } from '../../../../../utils/WorkDayUtils';
import { isOnVacationOn, vacationLastDayLabel, vacationReturnLabel, vacationStatus } from '../../../../../utils/VacationUtils';
import { findSpecialDay, specialDayDateLabel, specialDayHoursLabel } from '../../../../../utils/SpecialDayUtils';

// A API nova já aplica esta regra. O filtro também protege o site durante deploys
// em que alguma instância antiga do backend ainda devolva a grade interna de 10 minutos.
const filterPublicSlots = (slots) => (slots || []).filter((slot) => {
    const timePart = slot.startTime?.split('T')[1];
    if (!timePart) return false;

    const minute = Number(timePart.substring(3, 5));
    return Number.isInteger(minute) && minute % 30 === 0;
});

// Janela usada quando o backend ainda não informa a da empresa: era o número fixo do
// calendário antes de virar parâmetro (ParameterAppointment#publicBookingDays).
const DEFAULT_BOOKING_DAYS = 14;

const StepDateTime = ({ companyUuid, branchId, employeeId, duration, onSelect, onBack, selectedDate, selectedTime, employee, bookingDays, specialDays = [] }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [slots, setSlots] = useState([]);
    const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
    const [selected, setSelected] = useState({
        date: selectedDate || new Date(),
        time: selectedTime || null,
        assignedEmployee: null
    });
    const [error, setError] = useState('');
    const isRandomEmployee = employee?.isRandom === true;
    const candidateEmployees = isRandomEmployee ? (employee?.candidateEmployees || []) : [];
    // Nem todo colaborador tem foto cadastrada. Sem isso o círculo do strip ficava vazio;
    // as iniciais são o mesmo recurso usado nos cards do passo de escolha do profissional.
    const [photoFailed, setPhotoFailed] = useState(false);

    const employeeName = employee?.name || employee?.fullName || employee?.firstName || 'Profissional';

    // "Pedro Paulo" -> "PP"; nome único -> as duas primeiras letras.
    const getInitials = (name) => {
        const parts = (name || '').split(' ').filter(Boolean);

        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }

        return (name || '?').substring(0, 2).toUpperCase();
    };

    /**
     * Se há atendimento na data, olhando os dias cadastrados do profissional.
     *
     * Na escolha automática basta um candidato atender no dia para ele continuar clicável.
     */
    const hasEmployeeOnDate = (date) => {
        const employeesToCheck = isRandomEmployee ? candidateEmployees : [employee];

        if (employeesToCheck.length === 0) {
            return true;
        }

        // Férias fecham o dia do mesmo jeito que a folga; o backend já devolve a grade
        // vazia, e aqui o dia fica apagado para o cliente nem precisar clicar.
        return employeesToCheck.some(candidate => worksOnDate(candidate?.workDays, date)
            && !isOnVacationOn(candidate, date));
    };

    // Aviso de férias só para o profissional escolhido: na escolha automática sempre há
    // outro candidato, e citar as férias de alguém que o cliente nem escolheu confunde.
    const employeeVacationStatus = !isRandomEmployee ? vacationStatus(employee) : null;
    const showVacationNotice = employeeVacationStatus === 'active' || employeeVacationStatus === 'scheduled';

    // Dias liberados no calendário, contando hoje, conforme Configurações > Agendamento. O
    // backend confere a mesma janela ao criar o agendamento, então isto é só a exibição.
    const getDaysArray = () => {
        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const totalDays = Number(bookingDays) > 0 ? Number(bookingDays) : DEFAULT_BOOKING_DAYS;

        for (let i = 0; i < totalDays; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            days.push(date);
        }
        return days;
    };

    const [availableDays] = useState(getDaysArray());

    // Datas especiais da filial dentro dos dias do calendário. O dia fechado fica apagado como a
    // folga, e o aviso diz o motivo: sem ele o cliente via o feriado só como "sem horários".
    const closedSpecialDayOn = (date) => {
        const day = findSpecialDay(specialDays, date);
        return day && !day.isOpen ? day : null;
    };
    const visibleSpecialDays = availableDays
        .map((date) => findSpecialDay(specialDays, date))
        .filter(Boolean);
    const selectedClosedDay = closedSpecialDayOn(currentDate);

    useEffect(() => {
        if (currentDate) {
            loadSlots(currentDate);
        }
    }, [currentDate, employeeId]);

    const loadSlots = async (date) => {
        try {
            setLoading(true);
            setError('');
            const dateStr = formatDateForApi(date);
            if (isRandomEmployee) {
                const responses = await Promise.all(
                    candidateEmployees.map((candidate) =>
                        getAvailableSlots(companyUuid, branchId, candidate.id, dateStr, duration)
                            .then((response) => ({ candidate, slots: filterPublicSlots(response.data) }))
                    )
                );
                setSlots(mergeRandomEmployeeSlots(responses));
            } else {
                const response = await getAvailableSlots(companyUuid, branchId, employeeId, dateStr, duration);
                setSlots(filterPublicSlots(response.data));
            }
        } catch (err) {
            console.error("Error loading slots:", err);
            setError(t('booking.load_slots_error'));
            setSlots([]);
        } finally {
            setLoading(false);
        }
    };

    const mergeRandomEmployeeSlots = (employeeSlotResponses) => {
        const slotsByStartTime = new Map();

        employeeSlotResponses.forEach(({ candidate, slots: candidateSlots }) => {
            candidateSlots.forEach((slot) => {
                if (!slot.startTime) return;

                const existingSlot = slotsByStartTime.get(slot.startTime) || {
                    ...slot,
                    available: false,
                    availableEmployees: []
                };

                if (slot.available) {
                    existingSlot.available = true;
                    existingSlot.unavailabilityReason = null;
                    existingSlot.availableEmployees = [
                        ...(existingSlot.availableEmployees || []),
                        candidate
                    ];
                }

                slotsByStartTime.set(slot.startTime, existingSlot);
            });
        });

        return Array.from(slotsByStartTime.values())
            .sort((first, second) => new Date(first.startTime) - new Date(second.startTime));
    };

    const formatDateForApi = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDayName = (date) => {
        return date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
    };

    const formatDayNumber = (date) => {
        return date.getDate();
    };

    const formatMonthYear = (date) => {
        return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    };

    const formatSelectedDate = (date) => {
        const formatted = date.toLocaleDateString('pt-BR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
        return formatted.replace(/(^|, | de )(\p{L})/gu, (_, prefix, letter) => `${prefix}${letter.toUpperCase()}`);
    };

    const isToday = (date) => {
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    const isSameDay = (date1, date2) => {
        if (!date1 || !date2) return false;
        return date1.toDateString() === date2.toDateString();
    };

    const handleDaySelect = (date) => {
        setCurrentDate(date);
        setSelected(prev => ({ ...prev, date, time: null, assignedEmployee: null }));
    };

    const handleTimeSelect = (slot) => {
        if (slot.available) {
            const availableEmployees = slot.availableEmployees || [];
            const assignedEmployee = isRandomEmployee && availableEmployees.length > 0
                ? availableEmployees[Math.floor(Math.random() * availableEmployees.length)]
                : null;
            setSelected(prev => ({
                ...prev,
                date: prev.date || currentDate,
                time: slot.time,
                assignedEmployee
            }));
        }
    };

    const handleContinue = () => {
        if (selected.date && selected.time) {
            onSelect(selected.date, selected.time, selected.assignedEmployee);
        }
    };

    // Agrupa slots por período (manhã, tarde, noite)
    const groupSlotsByPeriod = () => {
        const morning = [];
        const afternoon = [];
        const evening = [];

        if (!slots || !Array.isArray(slots)) {
            return { morning, afternoon, evening };
        }

        slots.forEach(slot => {
            // Extrai a hora do startTime (formato: "2025-11-30T08:00:00")
            const startTime = slot.startTime;
            if (!startTime) return;
            
            // Extrai apenas a hora (HH:mm) do datetime
            const timePart = startTime.split('T')[1];
            if (!timePart) return;
            
            const time = timePart.substring(0, 5); // "08:00"
            const hour = parseInt(time.split(':')[0]);
            
            const slotObj = { 
                ...slot, 
                time: time // Adiciona o campo time formatado
            };
            
            if (hour < 12) {
                morning.push(slotObj);
            } else if (hour < 18) {
                afternoon.push(slotObj);
            } else {
                evening.push(slotObj);
            }
        });

        return { morning, afternoon, evening };
    };

    const { morning, afternoon, evening } = groupSlotsByPeriod();

    const renderTimeSlots = (periodSlots, periodName, icon) => {
        if (periodSlots.length === 0) return null;

        return (
            <div className="time-period mb-4">
                <h6 className="period-title text-muted mb-3">
                    <i className={`fa ${icon} me-2`}></i>
                    {periodName}
                </h6>
                <div className="time-slots d-flex flex-wrap gap-2">
                    {periodSlots.map((slot, index) => (
                        <Button
                            key={index}
                            variant={selected.time === slot.time ? 'primary' : slot.available ? 'outline-primary' : 'outline-secondary'}
                            className={`time-slot-btn ${!slot.available ? 'disabled' : ''}`}
                            onClick={() => handleTimeSelect(slot)}
                            disabled={!slot.available}
                            aria-pressed={selected.time === slot.time}
                        >
                            {slot.time}
                        </Button>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="step-datetime">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-calendar-alt me-2 text-primary"></i>
                    {t('booking.select_datetime')}
                </h4>
                <p className="step-subtitle text-muted">
                    {t('booking.select_datetime_description')}
                </p>
            </div>

            {/* Mini-strip: colaborador selecionado */}
            {employee && (
                <div className="datetime-employee-strip mb-4">
                    {isRandomEmployee ? (
                        <div className="datetime-emp-avatar datetime-emp-avatar-random">
                            <i className="fa fa-shuffle"></i>
                        </div>
                    ) : (
                        <div className="datetime-emp-avatar">
                            {photoFailed ? (
                                <span className="datetime-emp-initials">{getInitials(employeeName)}</span>
                            ) : (
                                <img
                                    src={getEmployeePhotoUrl(companyUuid, employee.id)}
                                    alt={employeeName}
                                    onError={() => setPhotoFailed(true)}
                                />
                            )}
                        </div>
                    )}
                    <div className="ms-3">
                        <small className="text-muted d-block">
                            {isRandomEmployee ? 'Escolha automática' : 'Profissional selecionado'}
                        </small>
                        <strong className="text-dark">
                            {isRandomEmployee ? 'Qualquer profissional disponível' : employeeName}
                        </strong>
                    </div>
                </div>
            )}

            {showVacationNotice && (
                <div className="booking-vacation-notice mb-3" role="status">
                    <i className="fa fa-umbrella-beach" aria-hidden="true"></i>
                    <span>
                        {employeeName} estará de férias até <strong>{vacationLastDayLabel(employee)}</strong> e
                        volta a atender em <strong>{vacationReturnLabel(employee)}</strong>. Esses dias ficam indisponíveis.
                    </span>
                </div>
            )}

            {visibleSpecialDays.length > 0 && (
                <div className="booking-vacation-notice mb-3" role="status">
                    <i className="fa fa-calendar-xmark" aria-hidden="true"></i>
                    <span>
                        Feriados e horários especiais: {visibleSpecialDays
                            .map((day) => `${specialDayDateLabel(day.specialDate)}: ${day.isOpen
                                ? `atendimento das ${specialDayHoursLabel(day)}`
                                : 'unidade fechada'} (${day.reason})`)
                            .join('; ')}.
                    </span>
                </div>
            )}

            {/* Seletor de dias */}
            <div className="days-selector mb-4">
                <div className="month-year text-center mb-3">
                    <span className="fw-semibold text-capitalize">
                        {formatMonthYear(currentDate)}
                    </span>
                </div>
                <div className="days-scroll d-flex gap-2 overflow-auto pb-2">
                    {availableDays.map((date, index) => {
                        const closedDay = closedSpecialDayOn(date);
                        const isDayOff = !hasEmployeeOnDate(date) || Boolean(closedDay);

                        return (
                        <button
                            key={index}
                            type="button"
                            className={`day-card text-center p-2 rounded ${isDayOff ? 'day-card-off' : 'cursor-pointer'} ${isSameDay(date, currentDate) ? 'selected' : ''}`}
                            onClick={() => !isDayOff && handleDaySelect(date)}
                            disabled={isDayOff}
                            aria-pressed={isSameDay(date, currentDate)}
                            aria-label={date.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
                                + (closedDay ? `, fechado (${closedDay.reason})` : '')}
                            title={closedDay ? `Fechado (${closedDay.reason})` : undefined}
                        >
                            <span className="day-name d-block text-uppercase small text-muted">
                                {formatDayName(date)}
                            </span>
                            <span className="day-number d-block fw-bold">
                                {formatDayNumber(date)}
                            </span>
                            {isToday(date) && (
                                <Badge bg="primary" className="today-badge" style={{ fontSize: '0.6rem' }}>
                                    {t('today')}
                                </Badge>
                            )}
                        </button>
                        );
                    })}
                </div>
            </div>

            {/* Horários disponíveis */}
            <div className="time-slots-container">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mt-2 text-muted small">{t('booking.loading_slots')}</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-4">
                        <p className="text-danger">{error}</p>
                    </div>
                ) : slots.length === 0 ? (
                    <div className="text-center py-4">
                        <i className="fa fa-calendar-times fa-2x text-muted mb-3"></i>
                        <p className="text-muted">
                            {selectedClosedDay
                                ? `A unidade estará fechada neste dia (${selectedClosedDay.reason}).`
                                : !isRandomEmployee && isOnVacationOn(employee, currentDate)
                                ? `${employeeName} está de férias neste dia. Volta a atender em ${vacationReturnLabel(employee)}.`
                                : t('booking.no_slots_available')}
                        </p>
                    </div>
                ) : (
                    <>
                        {renderTimeSlots(morning, t('booking.morning'), 'fa-sun')}
                        {renderTimeSlots(afternoon, t('booking.afternoon'), 'fa-cloud-sun')}
                        {renderTimeSlots(evening, t('booking.evening'), 'fa-moon')}
                    </>
                )}
            </div>

            {/* Resumo da seleção */}
            {selected.date && selected.time && (
                <div className="selection-summary p-3 bg-light rounded mb-4 mt-4">
                    <div className="d-flex align-items-center">
                        <i className="fa fa-check-circle text-success me-2"></i>
                        <span>
                            <strong>{formatSelectedDate(selected.date)}</strong>
                            {' '}{t('booking.at')}{' '}
                            <strong>{selected.time}</strong>
                        </span>
                    </div>
                </div>
            )}

            {/* Botões de navegação */}
            <div className="step-actions d-flex justify-content-between mt-4">
                <Button variant="outline-secondary" onClick={onBack}>
                    <i className="fa fa-arrow-left me-2"></i>
                    {t('back')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleContinue}
                    disabled={!selected.date || !selected.time}
                >
                    {t('continue')}
                    <i className="fa fa-arrow-right ms-2"></i>
                </Button>
            </div>
        </div>
    );
};

export default StepDateTime;
